import { readChartEligibility } from './chartEligibility';
import { encryptPayload, decryptPayload } from './crypto';
import { encryptVaultPayload, decryptVaultPayload, getVaultStatus } from './vault';
import { getOfflineOwnerIdentity } from './offlineIdentity';
import { commitVaultRecords, listVaultRecords, readVaultRecord } from './vaultStorage';

export const PREPARED_CHART_TTL_MS = 24 * 60 * 60 * 1000;
export const MAX_RECENT_PREPARED_CHARTS = 10;
export const MAX_PREPARED_BYTES = 100 * 1024 * 1024;
let additionalUsageReader = async () => 0;
let additionalChartsReader = null;
const retentionReconcilers = new Map();

export function registerPreparedRetentionReconciler(name, reconcile) {
  if (reconcile) retentionReconcilers.set(name, reconcile);
  else retentionReconcilers.delete(name);
}

async function reconcileRetainedWork(owner) {
  // Discover older retained drafts before making any eviction decision. A
  // failed discovery aborts cleanup rather than risking their dependencies.
  await Promise.all([...retentionReconcilers.values()].map(reconcile => reconcile(owner)));
}

export function registerAdditionalPreparedStorageUsage(reader) {
  additionalUsageReader = reader;
}

// Each descriptor owns a guarded remove callback. The canonical store stays
// independent while both formats share one client-level retention decision.
export function registerAdditionalPreparedCharts(reader) {
  additionalChartsReader = reader;
}

export async function getPreparedStorageBudget() {
  try {
    const estimate = await navigator.storage?.estimate?.();
    if (estimate?.quota) return Math.min(MAX_PREPARED_BYTES, Math.floor(estimate.quota * 0.1));
  } catch { /* Estimates are advisory; use the fixed maximum. */ }
  return MAX_PREPARED_BYTES;
}

export async function getPreparedBytes(owner = getOfflineOwnerIdentity()) {
  return (await listPreparedCharts(owner)).reduce((sum, manifest) => sum + manifest.bytes, 0);
}

export function preparedChartId(ownerKey, { clientReferenceId, pregnancyReferenceId }) {
  return JSON.stringify([ownerKey, String(clientReferenceId), String(pregnancyReferenceId || '')]);
}

export function normalizePreparedUrl(url) {
  return new URL(url, window.location.origin).href;
}

function assertOwner(owner) {
  if (!owner || getOfflineOwnerIdentity()?.ownerKey !== owner.ownerKey) throw new Error('Practice access changed during offline preparation. Please retry in the current chart.');
}

async function encryptPrepared(payload, owner) {
  const status = await getVaultStatus(owner);
  assertOwner(owner);
  // Existing installations can repair preparation before device enrollment.
  // Once enrolled, a locked vault must never fall back to the legacy key.
  return status.enrolled ? encryptVaultPayload(payload, owner) : encryptPayload(payload);
}

async function decryptPrepared(payload, owner) {
  if (payload.version === 2) return decryptVaultPayload(payload, owner);
  const status = await getVaultStatus(owner);
  if (status.enrolled && !status.unlocked) throw new Error('Unlock device charting before reading prepared records.');
  return decryptPayload(payload);
}

export async function getPreparedChartManifest(context, owner = getOfflineOwnerIdentity()) {
  if (!owner || !context.clientReferenceId) return null;
  return readVaultRecord('manifests', preparedChartId(owner.ownerKey, context));
}

export async function touchPreparedChart(context, owner = getOfflineOwnerIdentity()) {
  const manifest = await getPreparedChartManifest(context, owner);
  if (!manifest) return null;
  assertOwner(owner);
  const updated = { ...manifest, lastOpenedAt: Date.now() };
  await commitVaultRecords([{ store: 'manifests', expected: manifest, record: updated }]);
  return updated;
}

export async function listPreparedCharts(owner = getOfflineOwnerIdentity()) {
  if (!owner) return [];
  return (await listVaultRecords('manifests')).filter(item => item.ownerKey === owner.ownerKey);
}

export async function storePreparedChartResources(resources, context, { expiresAt, owner = getOfflineOwnerIdentity() } = {}) {
  assertOwner(owner);
  await reconcileRetainedWork(owner);
  assertOwner(owner);
  if (!context.clientReferenceId || !resources.length) throw new Error('A complete client chart is required before preparing offline access.');
  const id = preparedChartId(owner.ownerKey, context);
  const now = Date.now();
  let previous = await readVaultRecord('manifests', id);
  const pinId = `pins:${id}`;
  const generalPinId = `pins:${preparedChartId(owner.ownerKey, { clientReferenceId: context.clientReferenceId })}`;
  let pinIntent = await readVaultRecord('continuities', pinId);
  let generalPinIntent = context.pregnancyReferenceId ? await readVaultRecord('continuities', generalPinId) : null;
  const records = await Promise.all(resources.map(async resource => {
    const url = normalizePreparedUrl(resource.url);
    const payload = await encryptPrepared({ url, status: resource.status || 200,
      headers: resource.headers || { 'Content-Type': 'application/json' }, body: resource.body }, owner);
    return { id: JSON.stringify([id, url]), manifestId: id, ownerKey: owner.ownerKey, url, payload,
      bytes: new TextEncoder().encode(JSON.stringify(payload)).byteLength };
  }));
  assertOwner(owner);
  const manifest = { id, ownerKey: owner.ownerKey, clientReferenceId: String(context.clientReferenceId),
    pregnancyReferenceId: context.pregnancyReferenceId ? String(context.pregnancyReferenceId) : null,
    preparedAt: now, lastOpenedAt: now, expiresAt: Math.min(expiresAt || now + PREPARED_CHART_TTL_MS, now + PREPARED_CHART_TTL_MS),
    resourceIds: records.map(item => item.id), urls: records.map(item => item.url),
    bytes: records.reduce((total, item) => total + item.bytes, 0), pins: [...new Set([...(previous?.pins || []), ...(pinIntent?.pins || []), ...(generalPinIntent?.pins || []), ...(context.pins || [])])] };
  const budget = await getPreparedStorageBudget();
  const additionalCharts = additionalChartsReader ? await additionalChartsReader(owner) : null;
  const reservedBytes = additionalCharts ? additionalCharts.filter(item => item.pinned || item.clientReferenceId === String(context.clientReferenceId)).reduce((sum, item) => sum + item.bytes, 0)
    : await additionalUsageReader(owner);
  const otherPinnedBytes = (await listPreparedCharts(owner)).filter(item => item.id !== id && (item.pins?.length || item.clientReferenceId === String(context.clientReferenceId)))
    .reduce((sum, item) => sum + item.bytes, 0);
  if (!manifest.pins.length && manifest.bytes + otherPinnedBytes + reservedBytes > budget) {
    window.dispatchEvent(new CustomEvent('vela:offline-storage-pressure', { detail: { budget } }));
    throw new Error('Device storage is reserved for active episodes and unsynced work. Finish syncing or remove an older prepared chart before preparing another.');
  }
  const newIds = new Set(manifest.resourceIds);
  let committed = false;
  for (let attempt = 0; attempt < 5 && !committed; attempt += 1) {
    manifest.pins = [...new Set([...(previous?.pins || []), ...(pinIntent?.pins || []), ...(generalPinIntent?.pins || []), ...(context.pins || [])])];
    committed = await commitVaultRecords([
      ...(previous?.resourceIds || []).filter(oldId => !newIds.has(oldId)).map(oldId => ({ store: 'snapshots', delete: true, id: oldId })),
      ...records.map(record => ({ store: 'snapshots', record })),
      { store: 'manifests', record: manifest },
    ], { guards: [{ store: 'manifests', id, expected: previous }, { store: 'continuities', id: pinId, expected: pinIntent },
      ...(context.pregnancyReferenceId ? [{ store: 'continuities', id: generalPinId, expected: generalPinIntent }] : [])] }) !== false;
    if (!committed) {
      previous = await readVaultRecord('manifests', id);
      pinIntent = await readVaultRecord('continuities', pinId);
      generalPinIntent = context.pregnancyReferenceId ? await readVaultRecord('continuities', generalPinId) : null;
    }
  }
  if (!committed) throw new Error('This chart changed in another tab during preparation. Please retry.');
  assertOwner(owner);
  await cleanupPreparedCharts({ owner, preserveId: id });
  window.dispatchEvent(new CustomEvent('vela:offline-prepared-changed'));
  window.dispatchEvent(new CustomEvent('vela:offline-chart-prepared', { detail: { ...context, resources } }));
  return manifest;
}

export async function readPreparedResource(url, { clientReferenceId, allowExpired = false, activePregnancyReferences = null, owner = getOfflineOwnerIdentity() } = {}) {
  if (!owner || !clientReferenceId) return null;
  if ((await readChartEligibility(clientReferenceId))?.eligible !== true) return null;
  const normalized = normalizePreparedUrl(url);
  const manifests = (await listPreparedCharts(owner))
    .filter(item => item.clientReferenceId === String(clientReferenceId) && item.urls.includes(normalized)
      && (item.expiresAt > Date.now() || (allowExpired && item.pins.length > 0
        && (!activePregnancyReferences || activePregnancyReferences.includes(item.pregnancyReferenceId)))))
    .sort((a, b) => b.preparedAt - a.preparedAt);
  for (const manifest of manifests) {
    const record = await readVaultRecord('snapshots', JSON.stringify([manifest.id, normalized]));
    if (!record || record.ownerKey !== owner.ownerKey) continue;
    const resource = await decryptPrepared(record.payload, owner);
    assertOwner(owner);
    if (resource.url !== normalized) throw new Error('Prepared chart resource did not match its saved address.');
    return new Response(resource.body, { status: resource.status, headers: {
      ...resource.headers, 'X-Sena-Prepared-Offline': 'true', 'X-Sena-Prepared-At': new Date(manifest.preparedAt).toISOString(),
    } });
  }
  return null;
}

export async function setPreparedChartPin(context, reason, pinned = true, retentionOwner = null) {
  const owner = retentionOwner || getOfflineOwnerIdentity();
  if (!owner || !context.clientReferenceId) return;
  // Logout may clear credentials while retention discovery is still running.
  // A captured owner can only ADD metadata protection; it grants no read,
  // decryption or cross-owner unpin access.
  const assertPinOwner = () => { if (!retentionOwner || !pinned) assertOwner(owner); };
  assertPinOwner();
  const id = `pins:${preparedChartId(owner.ownerKey, context)}`;
  const updatePins = async (store, recordId, create = false) => {
    for (let attempt = 0; attempt < 5; attempt += 1) {
      const original = await readVaultRecord(store, recordId);
      if (!original && !create) return;
      const pins = new Set(original?.pins || []);
      if (pinned) pins.add(reason); else pins.delete(reason);
      assertPinOwner();
      if (await commitVaultRecords([{ store, record: { ...(original || { id: recordId, ownerKey: owner.ownerKey }), pins: [...pins] } }],
        { guard: { store, id: recordId, expected: original } }) !== false) return;
    }
    throw new Error('The chart storage changed in another tab. Please retry.');
  };
  await updatePins('continuities', id, true);
  const charts = await listPreparedCharts(owner);
  for (const manifest of charts.filter(item => item.clientReferenceId === String(context.clientReferenceId)
    && (!context.pregnancyReferenceId || item.pregnancyReferenceId === String(context.pregnancyReferenceId)))) {
    await updatePins('manifests', manifest.id);
  }
}

export async function cleanupPreparedCharts({ owner = getOfflineOwnerIdentity(), purge = false, releaseActiveLabor = false, preserveId = null, incomingChart = null } = {}) {
  if (!owner) return;
  await reconcileRetainedWork(owner);
  if (releaseActiveLabor) {
    // Explicit logout ends continuity. Pending work keeps its own pins, while
    // labor-only clean snapshots can be purged with the rest of the session.
    for (const store of ['manifests', 'continuities']) {
      for (const item of (await listVaultRecords(store)).filter(value => value.ownerKey === owner.ownerKey && value.pins?.some(pin => pin.startsWith('labor:')))) {
        await commitVaultRecords([{ store, record: { ...item, pins: item.pins.filter(pin => !pin.startsWith('labor:')) } }],
          { guard: { store, id: item.id, expected: item } });
      }
    }
  }
  const manifests = await listPreparedCharts(owner);
  const external = additionalChartsReader ? await additionalChartsReader(owner) : [];
  const reserve = additionalChartsReader ? 0 : await additionalUsageReader(owner);
  const budget = Math.max(0, (await getPreparedStorageBudget()) - reserve);
  const entries = [
    ...external.filter(item => !incomingChart || item.id !== incomingChart.id).map(item => ({ ...item, external: true })),
    ...manifests.map(item => ({
      id: item.id, clientReferenceId: item.clientReferenceId, bytes: item.bytes,
      lastOpenedAt: item.lastOpenedAt, expiresAt: item.expiresAt, pinned: Boolean(item.pins?.length),
      preserved: item.id === preserveId,
      remove: () => commitVaultRecords([
        ...item.resourceIds.map(id => ({ store: 'snapshots', delete: true, id })),
        { store: 'manifests', delete: true, id: item.id },
      ], { guard: { store: 'manifests', id: item.id, expected: item } }),
    })),
    ...(incomingChart ? [{ ...incomingChart, preserved: true, lastOpenedAt: Date.now(), expiresAt: Date.now() + PREPARED_CHART_TTL_MS }] : []),
  ];
  const removed = new Set();
  const groups = () => {
    const result = new Map();
    for (const item of entries.filter(value => !removed.has(value))) {
      const ref = item.clientReferenceId || `unknown:${item.id}`;
      const group = result.get(ref) || { entries: [], pinned: false, preserved: false, openedAt: 0 };
      group.entries.push(item); group.pinned ||= item.pinned;
      group.preserved ||= item.preserved; group.openedAt = Math.max(group.openedAt, item.lastOpenedAt || 0);
      result.set(ref, group);
    }
    return [...result.values()];
  };
  const stats = () => ({ bytes: entries.filter(item => !removed.has(item)).reduce((sum, item) => sum + item.bytes, 0),
    ordinaryChartCount: groups().filter(group => !group.pinned).length });
  const removeEntries = async items => {
    // Check the canonical CAS first. If it changed (for example, a new local
    // edit arrived), retain its prepared dependencies and try on a later pass.
    for (const item of [...items].sort((a, b) => Number(Boolean(b.external)) - Number(Boolean(a.external)))) {
      if (item.preserved || !item.remove || await item.remove() === false) return false;
      removed.add(item);
    }
    return true;
  };
  for (const group of groups()) {
    if (group.pinned || group.preserved) continue;
    await removeEntries(group.entries.filter(item => purge || item.expiresAt <= Date.now()));
  }
  for (const group of groups().filter(item => !item.pinned && !item.preserved).sort((a, b) => a.openedAt - b.openedAt)) {
    const current = stats();
    if (current.ordinaryChartCount <= MAX_RECENT_PREPARED_CHARTS && current.bytes <= budget) break;
    await removeEntries(group.entries);
  }
  const result = { ...stats(), budget };
  if (result.bytes > budget) window.dispatchEvent(new CustomEvent('vela:offline-storage-pressure', { detail: result }));
  return result;
}
